import React, { FC, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, TextField } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import { Address } from 'models/contact';
import AddressForm from 'components/shared/address/address-form.component';
import { LabDetailsFormData } from 'models/forms';
import { useFormik } from 'formik';
import { emptyLabDetailsForm } from './form-helper';
import { Organization } from 'models/organization';
import { isValidEmail } from 'utils/validations';
import CurrencyInput from 'components/shared/currency-input/currency-input.component';
import { Currency } from 'models/country';

type LabDetailsFormProps = {
    lab: Organization;
    onChange: (labForm: LabDetailsFormData, isFormValid: boolean) => void;
    currency: Currency;
    onCurrencySelect: (currency: Currency) => void;
};
const LabDetailsForm: FC<LabDetailsFormProps> = ({
    lab,
    onChange,
    currency,
    onCurrencySelect,
}: LabDetailsFormProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);

    const validate = (formData: LabDetailsFormData) => {
        const errors: Record<string, string> = {};
        if (!formData.name) {
            errors.name = translate('organization:VALIDATION:LAB_NAME');
        }
        if (formData.email && !isValidEmail(formData.email)) {
            errors.email = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        if (!formData.address || !formData.address.street) {
            errors.email = 'Address is required';
        }

        onChange(formData, Object.keys(errors).length === 0);
        return errors;
    };

    const formik = useFormik<LabDetailsFormData>({
        initialValues: emptyLabDetailsForm(),
        validate,
        onSubmit: async (formData) => {
            onChange(formData, formik.isValid);
        },
    });

    useEffect(() => {
        formik.setValues({
            name: lab.name,
            publishLab: lab.publishLab,
            address: lab.contact.address,
            phone: lab.contact.communication.phone || '',
            email: lab.contact.communication.email || '',
            site: lab.contact.communication.site || '',
        });
    }, [lab]);

    const handleAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('address', {
            ...address,
            deliveryInstructions: formik.values.address.deliveryInstructions,
        });
    };

    return (
        <Fragment>
            <Box mb={2}>
                <Grid container spacing={2}>
                    <Grid item sm={12} md={8}>
                        <TextField
                            fullWidth
                            label={translate('lab-settings:COMPANY_NAME')}
                            helperText={formik.touched.name && formik.errors.name}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            {...formik.getFieldProps('name')}
                        />
                    </Grid>
                    <Grid item sm={12} md={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formik.values.publishLab}
                                    onChange={(_, value) => formik.setFieldValue('publishLab', value)}
                                />
                            }
                            label={translate('lab-settings:PUBLISH_LAB_PROFILE')}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box mb={3}>
                {/* TODO: This field should be moved to the Transport services tab later... */}
                <Grid container spacing={2}>
                    <Grid item sm={12} md={6}>
                        <FormControl fullWidth>
                            <TextField
                                multiline
                                rows={1}
                                label={translate('lab-settings:DELIVERY_INSTRUCTIONS')}
                                helperText={
                                    formik.touched.address?.deliveryInstructions &&
                                    formik.errors.address?.deliveryInstructions
                                }
                                error={
                                    formik.touched.address?.deliveryInstructions &&
                                    Boolean(formik.errors.address?.deliveryInstructions)
                                }
                                {...formik.getFieldProps('address.deliveryInstructions')}
                            />
                            <FormHelperText id="delivery-instructions">
                                {translate('lab-settings:DELIVERY_INSTRUCTIONS_HELPER')}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item sm={12} md={6}>
                        <CurrencyInput currency={currency} onCurrencySelect={onCurrencySelect} />
                    </Grid>
                </Grid>
            </Box>

            <PageTitle headingStyle="h3" title={translate('lab-settings:CONTACT_DETAILS')} />
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <AddressForm address={lab.contact?.address} onChange={handleAddressChange} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={translate('common:EMAIL')}
                            helperText={formik.touched.email && formik.errors.email}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            {...formik.getFieldProps('email')}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={translate('common:PHONE')}
                            helperText={formik.touched.phone && formik.errors.phone}
                            error={formik.touched.phone && Boolean(formik.errors.phone)}
                            {...formik.getFieldProps('phone')}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={translate('common:WEBSITE')}
                            helperText={formik.touched.site && formik.errors.site}
                            error={formik.touched.site && Boolean(formik.errors.site)}
                            {...formik.getFieldProps('site')}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Fragment>
    );
};

export default LabDetailsForm;
